import { format } from "date-fns";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
	ExportValidationDialog,
	type InvalidMatter,
} from "@/features/attorney/components/ExportValidationDialog";
import {
	type EntryStatus,
	type EntryV2,
	useCacheMutations,
	useMattersCache,
	useSettingsCache,
} from "@/shared/cache";
import { useEntries } from "@/shared/contexts/EntriesContext";
import { getFirmDomain } from "@/shared/utils/firm";
import { logger } from "@/shared/utils/logger";
import { useApi } from "@/shared/utils/request";
import type { ActionFormSubmit } from "@/v2/components/action-dialog/ActionForm";
import { CollapseDialog } from "@/v2/components/CollapseDialog";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import { GroupDialog } from "@/v2/components/GroupDialog";
import { MergeDialog } from "@/v2/components/MergeDialog";
import { type AddPayload, addAction } from "@/v2/hooks/actions/add";
import {
	type CollapseOptions,
	collapseAction,
} from "@/v2/hooks/actions/collapse";
import { deleteAction } from "@/v2/hooks/actions/delete";
import { duplicateAction } from "@/v2/hooks/actions/duplicate";
import { type EditPayload, editAction } from "@/v2/hooks/actions/edit";
import { formatEntryId, parseEntryId } from "@/v2/hooks/actions/entryId";
import { type ExportResult, exportAction } from "@/v2/hooks/actions/export";
import { type GroupOptions, groupAction } from "@/v2/hooks/actions/group";
import { type MergeOptions, mergeAction } from "@/v2/hooks/actions/merge";
import { restoreDeletedAction } from "@/v2/hooks/actions/restore";
import {
	setStatusAction,
	setStatusManyAction,
} from "@/v2/hooks/actions/setStatus";
import {
	type ActionDeps,
	type ApiError,
	actingHeaders,
} from "@/v2/hooks/actions/types";
import { ActionQueue, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { entryV2ToLegacy, legacyDateToV2 } from "@/v2/lib/entryAdapters";
import {
	distinctValues,
	hasConflicts,
	unitsCandidates,
} from "@/v2/lib/reconcileSources";
import {
	MIXED_TIMEKEEPER_SELECTION_MESSAGE,
	selectionTimekeeperId,
} from "@/v2/lib/selectionTimekeeperId";
import { AddEntryDialog } from "@/v2/pages/Entries/components/AddEntryDialog";
import { EditEntryDialog } from "@/v2/pages/Entries/components/EditEntryDialog";

export type RowActions = {
	onEdit: (entry: EntryV2) => void;
	onDuplicate: (entries: EntryV2[]) => void;
	onExport: (entries: EntryV2[]) => void;
	onDelete: (entries: EntryV2[]) => void;
	onRestoreDeleted: (entries: EntryV2[]) => void;
	onMarkState: (entries: EntryV2[], target: EntryStatus) => void;
};

type Props = {
	selectedEntries: EntryV2[];
	clearSelection: () => void;
	timekeeperId?: string | null;
};

type AddEntryPayload = {
	id: number;
	date: string;
	matter: string;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	activity: string;
};

type EditEntryPayload = AddEntryPayload & {
	timekeeperId?: string | null;
};

function dateToYYYYMMDD(d: Date): string {
	return format(d, "yyyy-MM-dd");
}

export function useEntryActions({
	selectedEntries,
	clearSelection,
	timekeeperId,
}: Props) {
	const { makeApiRequest } = useApi();
	const { matters } = useMattersCache();
	const { settings } = useSettingsCache();
	const { refresh, optimisticUpdate } = useCacheMutations();
	// The legacy EntriesContext is what wraps the v1 AttorneyPage tree. We
	// reuse its `updateEntries` / `refreshRecords` for the action factories
	// so other v1 surfaces (Tracked, Exported, etc.) stay coherent when an
	// action runs from the /entries page. The /entries view itself reads from
	// the v2 cache via `useAllEntries`, not from this context.
	const { updateEntries, refreshRecords } = useEntries();

	const refreshEntriesV2 = useCallback(() => refresh("entries_v2"), [refresh]);

	// ── Action queue + stable deps ───────────────────────────
	//
	// One ActionQueue instance per mount of the entries page. The queue
	// serializes mutations and pipes a toast per action; failures roll back
	// the optimistic update against whichever cache it was applied to. Both
	// the legacy entry store and the v2 cache get optimistic updates so the
	// page UI and the rest of the app stay coherent.
	const depsRef = useRef<ActionDeps>({
		makeApiRequest,
		updateEntries,
		refreshRecords,
		updateEntriesV2: (updater) => optimisticUpdate("entries_v2", updater),
		refreshRecordsV2: refreshEntriesV2,
	});
	depsRef.current = {
		makeApiRequest,
		updateEntries,
		refreshRecords,
		updateEntriesV2: (updater) => optimisticUpdate("entries_v2", updater),
		refreshRecordsV2: refreshEntriesV2,
	};

	const stableDeps = useRef<ActionDeps>({
		makeApiRequest: (opts) => depsRef.current.makeApiRequest(opts),
		updateEntries: (updater) => depsRef.current.updateEntries(updater),
		refreshRecords: () => depsRef.current.refreshRecords(),
		updateEntriesV2: (updater) =>
			depsRef.current.updateEntriesV2?.(updater) ?? (() => undefined),
		refreshRecordsV2: () =>
			depsRef.current.refreshRecordsV2?.() ?? Promise.resolve(),
	}).current;

	const [, setQueueSize] = useState(0);
	const queueRef = useRef<ActionQueue | null>(null);
	if (!queueRef.current) {
		queueRef.current = new ActionQueue(setQueueSize);
	}
	const enqueue = useCallback((action: QueuedAction) => {
		queueRef.current?.enqueue(action);
	}, []);

	// ── Dialog state (this is the only thing the page owns beyond
	// dispatching into the queue) ────────────────────────────
	const [addOpen, setAddOpen] = useState(false);
	const [addDefaultDate, setAddDefaultDate] = useState(() => new Date());
	const [addTimerPrefillDuration, setAddTimerPrefillDuration] = useState<
		number | null
	>(null);
	const [editOpen, setEditOpen] = useState(false);
	const [selectedEntry, setSelectedEntry] = useState<EntryV2 | null>(null);

	const [mergeOpen, setMergeOpen] = useState(false);
	const [groupOpen, setGroupOpen] = useState(false);
	const [collapseOpen, setCollapseOpen] = useState(false);
	const [mergeConflicts, setMergeConflicts] = useState<
		{ field: string; values: string[] }[] | undefined
	>(undefined);
	const [groupConflicts, setGroupConflicts] = useState<
		{ field: string; values: string[] }[] | undefined
	>(undefined);

	const [exportValidationOpen, setExportValidationOpen] = useState(false);
	const [invalidMatters, setInvalidMatters] = useState<InvalidMatter[]>([]);
	const [pendingExportIds, setPendingExportIds] = useState<number[]>([]);
	const [pendingExportTimekeeperId, setPendingExportTimekeeperId] = useState<
		string | null
	>(null);

	// ── Per-row export errors ───────────────────────────────────
	//
	// Session-scoped map of `ent_<n>` → failure reason, mirroring the legacy
	// the Entries page's `ActionQueueProvider.failedExports`. There's no server
	// field for this, so it lives in page state: a failed export draws the red
	// ribbon on the row (EntriesTable) and the "Export Failed" banner inside the
	// edit dialog. Cleared when the row re-exports successfully or
	// is edited.
	const [failedExports, setFailedExports] = useState<Map<string, string>>(
		new Map(),
	);

	const handleExportComplete = useCallback((result: ExportResult) => {
		setFailedExports((prev) => {
			const next = new Map(prev);
			for (const id of result.exportedIds) next.delete(formatEntryId(id));
			for (const { id, error } of result.failedEntries)
				next.set(formatEntryId(id), error);
			return next;
		});
	}, []);

	const clearError = useCallback((id: number) => {
		setFailedExports((prev) => {
			const key = formatEntryId(id);
			if (!prev.has(key)) return prev;
			const next = new Map(prev);
			next.delete(key);
			return next;
		});
	}, []);

	// ── Export (v2 atomic bulk) ─────────────────────────────────
	//
	// Export now runs through the same action queue as every other entry
	// mutation: `POST /v2/entries/export` flips the whole selection to the
	// terminal `exported` state in one call. The queue owns the toast and the
	// in-flight/loading state, so the old per-row `exportingIds` set (which
	// guarded opening the edit dialog on an in-flight row) is gone.
	//
	// Matter validation stays a FE-only pre-check: `POST /records/validateExport`
	// surfaces the ExportValidationDialog when matters look incomplete. Once it
	// passes — or the user proceeds anyway — we enqueue the export action.
	const performExport = useCallback(
		(ids: number[], timekeeperId?: string | null) => {
			if (ids.length === 0) return;
			enqueue(
				exportAction(
					ids,
					stableDeps,
					handleExportComplete,
					undefined,
					timekeeperId,
				),
			);
			clearSelection();
		},
		[enqueue, stableDeps, clearSelection, handleExportComplete],
	);

	const runValidatedExport = useCallback(
		async (ids: number[], timekeeperId?: string | null) => {
			if (ids.length === 0) return;
			if (getFirmDomain() !== "tysonmendes") {
				try {
					const resp = await makeApiRequest({
						endpoint: "/records/validateExport",
						method: "POST",
						body: { ids },
						headers: actingHeaders(timekeeperId),
					});
					if (resp.data?.invalidMatters?.length > 0) {
						setPendingExportIds(ids);
						setPendingExportTimekeeperId(timekeeperId ?? null);
						setInvalidMatters(resp.data.invalidMatters);
						setExportValidationOpen(true);
						return;
					}
				} catch {
					// proceed silently on validation failure
				}
			}
			performExport(ids, timekeeperId);
		},
		[makeApiRequest, performExport],
	);

	// ── State transitions ───────────────────────────────────
	//
	// Tracked ↔ reviewed flips use the cheap-path PATCH (single round-trip
	// per row, optimistic). Deletes go through the atomic v2 bulk endpoint
	// (`POST /v2/entries/delete`) — same backend leg as the PATCH, but a
	// single request covers any selection size. Exports stay on the v1
	// path until Phase 2 wires the export module through PATCH.
	const setStatus = useCallback(
		(
			id: number,
			target: "tracked" | "reviewed",
			timekeeperId?: string | null,
		) => {
			enqueue(setStatusAction(id, target, stableDeps, undefined, timekeeperId));
		},
		[enqueue, stableDeps],
	);

	const performSetStatus = useCallback(
		(
			ids: number[],
			target: "tracked" | "reviewed",
			timekeeperId?: string | null,
		) => {
			if (ids.length === 0) return;
			enqueue(
				setStatusManyAction(ids, target, stableDeps, undefined, timekeeperId),
			);
		},
		[enqueue, stableDeps],
	);

	const performDelete = useCallback(
		(ids: number[], timekeeperId?: string | null) => {
			if (ids.length === 0) return;
			enqueue(deleteAction(ids, stableDeps, timekeeperId));
		},
		[enqueue, stableDeps],
	);

	const performRestoreDeleted = useCallback(
		(ids: number[], timekeeperId?: string | null) => {
			if (ids.length === 0) return;
			enqueue(restoreDeletedAction(ids, stableDeps, timekeeperId));
		},
		[enqueue, stableDeps],
	);

	const markState = useCallback(
		(entry: EntryV2, target: EntryStatus) => {
			if (entry.status === target) return;
			const id = parseEntryId(entry.id);
			switch (target) {
				case "tracked":
				case "reviewed":
					setStatus(id, target, entry.timekeeperId);
					return;
				case "deleted":
					performDelete([id], entry.timekeeperId);
					return;
				case "exported":
					void runValidatedExport([id], entry.timekeeperId);
					return;
			}
		},
		[runValidatedExport, performDelete, setStatus],
	);

	// ── Duplicate (v2 atomic bulk) ──────────────────────────
	const performDuplicate = useCallback(
		(ids: number[], timekeeperId?: string | null) => {
			if (ids.length === 0) return;
			enqueue(duplicateAction(ids, stableDeps, timekeeperId));
			clearSelection();
		},
		[enqueue, stableDeps, clearSelection],
	);

	// ── Add ────────────────────────────────────────────────
	//
	// `shouldExport` on the page-level call is handled HERE — not via
	// `AddPayload.shouldExport`, which uses the un-validated v1 export path.
	// We enqueue a plain add and chain `runValidatedExport` on success so
	// add-then-export goes through the matter-validation dialog just like the
	// toolbar's Export button does.
	const handleSaveAdd = useCallback(
		(newEntry: AddEntryPayload, shouldExport: boolean) => {
			const payload: AddPayload = {
				...newEntry,
				date: legacyDateToV2(newEntry.date),
				shouldExport: false,
				timekeeperId: timekeeperId ?? null,
			};
			enqueue(
				addAction(
					payload,
					stableDeps,
					(newEntryId) => {
						if (shouldExport && newEntryId != null) {
							void runValidatedExport([newEntryId], payload.timekeeperId);
						}
					},
					(id, error) => {
						logger.captureException(new Error(error), {
							action: "addEntry-export",
							entryId: id,
						});
					},
				),
			);
		},
		[enqueue, stableDeps, runValidatedExport, timekeeperId],
	);

	// ── Edit ───────────────────────────────────────────────
	const handleSaveEdit = useCallback(
		(
			updated: EditEntryPayload,
			shouldExport: boolean,
			nextStatus?: EntryStatus,
		) => {
			const payload: EditPayload = {
				...updated,
				date: legacyDateToV2(updated.date),
				timekeeperId:
					updated.timekeeperId ?? selectedEntry?.timekeeperId ?? null,
			};
			const base = editAction(payload, stableDeps);
			// Chain validated export on success without changing editAction's
			// signature. The base action's onSuccess refreshes both caches; we
			// fire-and-forget the export afterward.
			const augmented: QueuedAction = {
				...base,
				onSuccess: () => {
					base.onSuccess?.();
					// A successful edit supersedes a prior export failure for this
					// row — drop its ribbon/banner. A re-export (shouldExport) that
					// fails again will re-populate the map via handleExportComplete.
					clearError(updated.id);
					if (
						!shouldExport &&
						(nextStatus === "tracked" || nextStatus === "reviewed") &&
						selectedEntry?.status !== nextStatus
					) {
						enqueue(
							setStatusAction(
								updated.id,
								nextStatus,
								stableDeps,
								undefined,
								payload.timekeeperId,
							),
						);
						setSelectedEntry(
							selectedEntry ? { ...selectedEntry, status: nextStatus } : null,
						);
					}
					if (shouldExport)
						void runValidatedExport([updated.id], payload.timekeeperId);
				},
			};
			enqueue(augmented);
		},
		[enqueue, stableDeps, runValidatedExport, clearError, selectedEntry],
	);

	// ── Merge / Group / Collapse ───────────────────────────
	const performBulkAction = useCallback(
		(
			kind: "merge" | "group" | "collapse",
			sources: EntryV2[],
			options: MergeOptions | GroupOptions | CollapseOptions,
			onApiError?: (err: ApiError) => void,
			mainSourceId?: number,
		) => {
			if (sources.length === 0) return;
			const timekeeperId = selectionTimekeeperId(sources);
			if (timekeeperId === false) {
				toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
				return;
			}
			const ids = sources.map((e) => parseEntryId(e.id));
			let action: QueuedAction;
			if (kind === "merge") {
				action = mergeAction(
					ids,
					options as MergeOptions,
					stableDeps,
					onApiError,
					timekeeperId,
				);
			} else if (kind === "group") {
				action = groupAction(
					ids,
					options as GroupOptions,
					stableDeps,
					onApiError,
					timekeeperId,
				);
			} else {
				if (mainSourceId == null) {
					throw new Error("collapse requires a mainSourceId");
				}
				action = collapseAction(
					ids,
					mainSourceId,
					options as CollapseOptions,
					stableDeps,
					onApiError,
					timekeeperId,
				);
			}
			enqueue(action);
			clearSelection();
		},
		[enqueue, stableDeps, clearSelection],
	);

	// ── Toolbar entrypoints ─────────────────────────────────
	const closeAddDialog = useCallback(() => {
		setAddOpen(false);
		setAddTimerPrefillDuration(null);
	}, []);

	const handleAddOpenChange = useCallback((open: boolean) => {
		setAddOpen(open);
		if (open) {
			setAddDefaultDate(new Date());
		}
		if (!open) {
			setAddTimerPrefillDuration(null);
		}
	}, []);

	const handleAddSubmit = useCallback(
		(values: TimeEntryFormValues) => {
			handleSaveAdd(
				{
					id: 0,
					date: format(values.date, "MM/dd/yyyy"),
					matter: values.matter,
					matter_id: values.matter_id,
					description: values.description,
					duration: values.duration,
					task: values.task,
					activity: values.activity ?? "",
				},
				false,
			);
			closeAddDialog();
		},
		[handleSaveAdd, closeAddDialog],
	);

	const onAdd = useCallback(() => {
		setAddDefaultDate(new Date());
		setAddTimerPrefillDuration(null);
		setAddOpen(true);
	}, []);

	// Open the Add dialog prefilled with the actual tracked duration (seconds) —
	// used by the toolbar timer when an entry is created from tracked time.
	const onAddWithDuration = useCallback((durationSeconds: number) => {
		setAddDefaultDate(new Date());
		setAddTimerPrefillDuration(durationSeconds > 0 ? durationSeconds : null);
		setAddOpen(true);
	}, []);

	// All-or-nothing semantics: if ANY selected row is exported, every modify
	// action (Merge, Group, Collapse, Delete, Export) is locked out. The
	// toolbar buttons disable in EntriesView via `selectionHasExported`; the
	// handlers below short-circuit defensively so a keyboard shortcut or
	// race can't bypass the visual disable. Duplicate is the only bulk
	// action allowed on exported rows.
	const selectionHasExported = useMemo(
		() => selectedEntries.some((e) => e.status === "exported"),
		[selectedEntries],
	);
	const selectedDeletedCount = useMemo(
		() => selectedEntries.filter((e) => e.status === "deleted").length,
		[selectedEntries],
	);
	const selectionHasDeleted = selectedDeletedCount > 0;

	const onExportAction = useCallback(() => {
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to export");
			return;
		}
		if (selectionHasDeleted) {
			toast.error("Restore entries before exporting");
			return;
		}
		if (selectionHasExported) return; // defense-in-depth — button is disabled
		const timekeeperId = selectionTimekeeperId(selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		const ids = selectedEntries.map((e) => parseEntryId(e.id));
		void runValidatedExport(ids, timekeeperId);
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasExported,
		runValidatedExport,
	]);

	const onRestoreDeletedAction = useCallback(() => {
		const ids = selectedEntries
			.filter((e) => e.status === "deleted")
			.map((e) => parseEntryId(e.id));
		if (ids.length === 0) {
			toast.error("Please select deleted entries to restore");
			return;
		}
		const deletedEntries = selectedEntries.filter(
			(e) => e.status === "deleted",
		);
		const timekeeperId = selectionTimekeeperId(deletedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		performRestoreDeleted(ids, timekeeperId);
	}, [selectedEntries, performRestoreDeleted]);

	const onDeleteAction = useCallback(() => {
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to delete");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasExported) return;
		const timekeeperId = selectionTimekeeperId(selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		const ids = selectedEntries.map((e) => parseEntryId(e.id));
		performDelete(ids, timekeeperId);
		clearSelection();
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasExported,
		performDelete,
		clearSelection,
	]);

	const onMarkReviewedAction = useCallback(() => {
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to mark reviewed");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasExported) return;
		const timekeeperId = selectionTimekeeperId(selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		const ids = selectedEntries.map((e) => parseEntryId(e.id));
		performSetStatus(ids, "reviewed", timekeeperId);
		clearSelection();
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasExported,
		performSetStatus,
		clearSelection,
	]);

	const onMarkTrackedAction = useCallback(() => {
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to mark tracked");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasExported) return;
		const timekeeperId = selectionTimekeeperId(selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		const ids = selectedEntries.map((e) => parseEntryId(e.id));
		performSetStatus(ids, "tracked", timekeeperId);
		clearSelection();
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasExported,
		performSetStatus,
		clearSelection,
	]);

	const onDuplicateAction = useCallback(() => {
		// Duplicate is the one bulk action that's valid on every state —
		// including exported. v1 `/records/duplicate` produces a fresh
		// `tracked` copy regardless of source state.
		if (selectedEntries.length === 0) {
			toast.error("Please select entries to duplicate");
			return;
		}
		const timekeeperId = selectionTimekeeperId(selectedEntries);
		if (timekeeperId === false) {
			toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
			return;
		}
		const ids = selectedEntries.map((e) => parseEntryId(e.id));
		performDuplicate(ids, timekeeperId);
	}, [selectedEntries, performDuplicate]);

	const onMerge = useCallback(() => {
		if (selectedEntries.length < 2) {
			toast.error("Please select at least 2 entries to merge");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasExported) return;
		const legacy = selectedEntries.map(entryV2ToLegacy);
		setMergeConflicts(undefined);
		const matterIds = distinctValues(legacy, "matter_id");
		const client = matterIds[0]
			? (matters.find((m) => m.matter_id === String(matterIds[0]))?.client ??
				null)
			: null;
		if (!hasConflicts(legacy, "merge", client)) {
			const taskCodes = distinctValues(legacy, "task");
			const activityCodes = distinctValues(legacy, "activity");
			const { sumUnits } = unitsCandidates(legacy, client);
			performBulkAction(
				"merge",
				selectedEntries,
				{
					date: selectedEntries[0].date,
					matterId: matterIds[0] ? String(matterIds[0]) : null,
					taskId: taskCodes[0] ? String(taskCodes[0]) : null,
					activityId: activityCodes[0] ? String(activityCodes[0]) : null,
					unitsSeconds: sumUnits.snappedSeconds,
				},
				(err) => {
					setMergeConflicts(err.conflicts);
					setMergeOpen(true);
				},
			);
			return;
		}
		setMergeOpen(true);
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasExported,
		matters,
		performBulkAction,
	]);

	const onGroup = useCallback(() => {
		if (selectedEntries.length < 2) {
			toast.error("Please select at least 2 entries to group");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasExported) return;
		const legacy = selectedEntries.map(entryV2ToLegacy);
		setGroupConflicts(undefined);
		const matterIds = distinctValues(legacy, "matter_id");
		const client = matterIds[0]
			? (matters.find((m) => m.matter_id === String(matterIds[0]))?.client ??
				null)
			: null;
		if (!hasConflicts(legacy, "group", client)) {
			const taskCodes = distinctValues(legacy, "task");
			const activityCodes = distinctValues(legacy, "activity");
			const { sumUnits } = unitsCandidates(legacy, client);
			performBulkAction(
				"group",
				selectedEntries,
				{
					date: selectedEntries[0].date,
					matterId: matterIds[0] ? String(matterIds[0]) : null,
					taskId: taskCodes[0] ? String(taskCodes[0]) : null,
					activityId: activityCodes[0] ? String(activityCodes[0]) : null,
					unitsSeconds: sumUnits.snappedSeconds,
					includeUnits: settings.p_group_include_units,
				},
				(err) => {
					setGroupConflicts(err.conflicts);
					setGroupOpen(true);
				},
			);
			return;
		}
		setGroupOpen(true);
	}, [
		selectedEntries,
		selectionHasDeleted,
		selectionHasExported,
		matters,
		performBulkAction,
		settings,
	]);

	const onCollapse = useCallback(() => {
		if (selectedEntries.length < 2) {
			toast.error("Please select at least 2 entries to collapse");
			return;
		}
		if (selectionHasDeleted) return;
		if (selectionHasExported) return;
		setCollapseOpen(true);
	}, [selectedEntries.length, selectionHasDeleted, selectionHasExported]);

	const onRowClick = useCallback((entry: EntryV2) => {
		// The action queue owns the in-flight/loading state now, so there's no
		// per-row export guard here — opening the edit dialog on a row that's
		// mid-export is harmless; the server rejects edits on exported rows and
		// the queue reconciles.
		setSelectedEntry(entry);
		setEditOpen(true);
	}, []);

	// Refs to keep rowActions stable across renders. RowContextMenuContent
	// mounts on every row, so an unstable rowActions reference would re-run
	// each row's menu component on every parent render.
	const onRowClickRef = useRef(onRowClick);
	const performDuplicateRef = useRef(performDuplicate);
	const runValidatedExportRef = useRef(runValidatedExport);
	const performDeleteRef = useRef(performDelete);
	const performRestoreDeletedRef = useRef(performRestoreDeleted);
	const performSetStatusRef = useRef(performSetStatus);
	const markStateRef = useRef(markState);
	const clearSelectionRef = useRef(clearSelection);
	onRowClickRef.current = onRowClick;
	performDuplicateRef.current = performDuplicate;
	runValidatedExportRef.current = runValidatedExport;
	performDeleteRef.current = performDelete;
	performRestoreDeletedRef.current = performRestoreDeleted;
	performSetStatusRef.current = performSetStatus;
	markStateRef.current = markState;
	clearSelectionRef.current = clearSelection;

	const rowActions = useMemo<RowActions>(
		() => ({
			onEdit: (entry) => onRowClickRef.current(entry),
			onDuplicate: (entries) => {
				const timekeeperId = selectionTimekeeperId(entries);
				if (timekeeperId === false) {
					toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
					return;
				}
				performDuplicateRef.current(
					entries.map((entry) => parseEntryId(entry.id)),
					timekeeperId,
				);
			},
			onExport: (entries) => {
				const activeEntries = entries.filter(
					(entry) => entry.status !== "deleted",
				);
				const timekeeperId = selectionTimekeeperId(activeEntries);
				if (timekeeperId === false) {
					toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
					return;
				}
				runValidatedExportRef.current(
					activeEntries.map((entry) => parseEntryId(entry.id)),
					timekeeperId,
				);
			},
			onDelete: (entries) => {
				const activeEntries = entries.filter(
					(entry) => entry.status !== "deleted",
				);
				const timekeeperId = selectionTimekeeperId(activeEntries);
				if (timekeeperId === false) {
					toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
					return;
				}
				performDeleteRef.current(
					activeEntries.map((entry) => parseEntryId(entry.id)),
					timekeeperId,
				);
				if (entries.length > 1) clearSelectionRef.current();
			},
			onRestoreDeleted: (entries) => {
				const deletedEntries = entries.filter(
					(entry) => entry.status === "deleted",
				);
				const timekeeperId = selectionTimekeeperId(deletedEntries);
				if (timekeeperId === false) {
					toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
					return;
				}
				performRestoreDeletedRef.current(
					deletedEntries.map((entry) => parseEntryId(entry.id)),
					timekeeperId,
				);
			},
			onMarkState: (entries, target) => {
				if (entries.length === 1) {
					markStateRef.current(entries[0], target);
					return;
				}
				const ids = entries
					.filter((entry) => entry.status !== target)
					.map((entry) => parseEntryId(entry.id));
				switch (target) {
					case "tracked":
					case "reviewed":
						{
							const timekeeperId = selectionTimekeeperId(entries);
							if (timekeeperId === false) {
								toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
								return;
							}
							performSetStatusRef.current(ids, target, timekeeperId);
						}
						clearSelectionRef.current();
						return;
					case "deleted":
						{
							const timekeeperId = selectionTimekeeperId(entries);
							if (timekeeperId === false) {
								toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
								return;
							}
							performDeleteRef.current(ids, timekeeperId);
						}
						clearSelectionRef.current();
						return;
					case "exported":
						{
							const timekeeperId = selectionTimekeeperId(entries);
							if (timekeeperId === false) {
								toast.error(MIXED_TIMEKEEPER_SELECTION_MESSAGE);
								return;
							}
							void runValidatedExportRef.current(ids, timekeeperId);
						}
						return;
				}
			},
		}),
		[],
	);

	const legacySelectedForDialogs = useMemo(
		() => selectedEntries.map(entryV2ToLegacy),
		[selectedEntries],
	);

	const editDialogEntry =
		editOpen && selectedEntry ? entryV2ToLegacy(selectedEntry) : null;

	const editDialogFailedExports = useMemo(() => {
		const next = new Map<number, string>();
		for (const [key, error] of failedExports)
			next.set(parseEntryId(key), error);
		return next;
	}, [failedExports]);

	const saveEditDialogValues = useCallback(
		(
			entryId: number,
			values: TimeEntryFormValues,
			shouldExport: boolean,
			nextStatus?: EntryStatus,
		) => {
			handleSaveEdit(
				{
					id: entryId,
					date: values.date ? format(values.date, "MM/dd/yyyy") : "",
					matter: values.matter,
					matter_id: values.matter_id,
					description: values.description,
					duration: values.duration,
					task: values.task,
					activity: values.activity ?? "",
				},
				shouldExport,
				nextStatus,
			);
			setEditOpen(false);
		},
		[handleSaveEdit],
	);

	const dialogs: ReactNode = (
		<>
			<AddEntryDialog
				open={addOpen || addTimerPrefillDuration !== null}
				date={addDefaultDate}
				timerPrefillDuration={addTimerPrefillDuration}
				onOpenChange={handleAddOpenChange}
				onSubmit={handleAddSubmit}
			/>
			<EditEntryDialog
				entry={editDialogEntry}
				failedExports={editDialogFailedExports}
				status={selectedEntry?.status}
				onOpenChange={(open) => {
					setEditOpen(open);
					if (!open) setSelectedEntry(null);
				}}
				onResolveFeedback={() => undefined}
				onSubmit={(entry, values, status) =>
					saveEditDialogValues(entry.id, values, false, status)
				}
				onExport={(entry, values) =>
					saveEditDialogValues(entry.id, values, true)
				}
				onDuplicate={(entry) => {
					performDuplicate([entry.id], entry.timekeeperId);
					setEditOpen(false);
					setSelectedEntry(null);
				}}
				onDelete={(entry) => {
					performDelete([entry.id], entry.timekeeperId);
					setEditOpen(false);
					setSelectedEntry(null);
				}}
				onRestore={(entry) => {
					performRestoreDeleted([entry.id], entry.timekeeperId);
					setSelectedEntry(
						selectedEntry ? { ...selectedEntry, status: "tracked" } : null,
					);
				}}
			/>
			{mergeOpen && (
				<MergeDialog
					open={mergeOpen}
					onOpenChange={(open) => {
						setMergeOpen(open);
						if (!open) setMergeConflicts(undefined);
					}}
					entries={legacySelectedForDialogs}
					conflicts={mergeConflicts}
					onSubmit={(values: ActionFormSubmit) => {
						setMergeOpen(false);
						setMergeConflicts(undefined);
						performBulkAction(
							"merge",
							selectedEntries,
							{
								date: dateToYYYYMMDD(values.date),
								matterId: values.matterId,
								taskId: values.taskId,
								activityId: values.activityId,
								unitsSeconds: values.unitsSeconds,
							},
							(err) => {
								setMergeConflicts(err.conflicts);
								setMergeOpen(true);
							},
						);
					}}
				/>
			)}
			{groupOpen && (
				<GroupDialog
					open={groupOpen}
					onOpenChange={(open) => {
						setGroupOpen(open);
						if (!open) setGroupConflicts(undefined);
					}}
					entries={legacySelectedForDialogs}
					conflicts={groupConflicts}
					defaultIncludeUnits={settings.p_group_include_units}
					onSubmit={(values: ActionFormSubmit) => {
						setGroupOpen(false);
						setGroupConflicts(undefined);
						performBulkAction(
							"group",
							selectedEntries,
							{
								date: dateToYYYYMMDD(values.date),
								matterId: values.matterId,
								taskId: values.taskId,
								activityId: values.activityId,
								unitsSeconds: values.unitsSeconds,
								includeUnits:
									values.includeUnits ?? settings.p_group_include_units,
							},
							(err) => {
								setGroupConflicts(err.conflicts);
								setGroupOpen(true);
							},
						);
					}}
				/>
			)}
			{collapseOpen && (
				<CollapseDialog
					open={collapseOpen}
					onOpenChange={setCollapseOpen}
					entries={legacySelectedForDialogs}
					onSubmit={({ mainSourceId, ...values }) => {
						setCollapseOpen(false);
						performBulkAction(
							"collapse",
							selectedEntries,
							{
								date: dateToYYYYMMDD(values.date),
								matterId: values.matterId,
								taskId: values.taskId,
								activityId: values.activityId,
								unitsSeconds: values.unitsSeconds,
							},
							undefined,
							mainSourceId,
						);
					}}
				/>
			)}
			{exportValidationOpen && (
				<ExportValidationDialog
					open={exportValidationOpen}
					onOpenChange={setExportValidationOpen}
					invalidMatters={invalidMatters}
					onProceed={() => {
						setExportValidationOpen(false);
						performExport(pendingExportIds, pendingExportTimekeeperId);
						setPendingExportIds([]);
						setPendingExportTimekeeperId(null);
						setInvalidMatters([]);
					}}
					onCancel={() => {
						setExportValidationOpen(false);
						setPendingExportIds([]);
						setPendingExportTimekeeperId(null);
						setInvalidMatters([]);
					}}
				/>
			)}
		</>
	);

	return {
		onAdd,
		onAddWithDuration,
		onExport: onExportAction,
		onRestoreDeleted: onRestoreDeletedAction,
		onDelete: onDeleteAction,
		onDuplicate: onDuplicateAction,
		onMarkReviewed: onMarkReviewedAction,
		onMarkTracked: onMarkTrackedAction,
		onMerge,
		onGroup,
		onCollapse,
		onRowClick,
		rowActions,
		dialogs,
		// EntriesView reads these to swap Export → Restore and to disable
		// normal modify toolbar buttons while selected rows are exported/deleted.
		// Duplicate stays clickable, since v1 `/records/duplicate` produces a
		// fresh `tracked` copy from any source.
		selectionHasExported,
		selectionHasDeleted,
		selectedDeletedCount,
	};
}
